<script lang="ts">
  import { storyblokEditable } from "@storyblok/svelte";
  import GridContainer from "$lib/molecules/GridContainer.svelte";
  import GridItem from "$lib/atoms/GridItem.svelte";
  import { parseUrl } from "$lib/utils/helpers";
  import type { HeroNewsroomStoryblok } from "$types/components";
  import { onMount } from "svelte";
  import gsap from "gsap";
  import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
  import Icon from "$lib/atoms/Icon.svelte";
  import { ButtonColor, IconType } from "$types/atoms";
  import { BREAKPOINTS } from "$lib/utils/breakpoints";
  import TiltedGallery from "$lib/molecules/TiltedGallery.svelte";

  export let blok: HeroNewsroomStoryblok;

  let wrapper: HTMLElement;
  let inner: HTMLElement;
  let wrapperHeight: number = 0;
  let scroller: ScrollTrigger;
  let desktopMediaQuery: MediaQueryList;
  let isMobile = true;

  export let theme = "grey";
  let backgroundColor = "bg-green";

  if (theme === "green") {
    backgroundColor = "bg-green";
  } else if (theme === "yellow") {
    backgroundColor = "bg-yellow";
  } else {
    backgroundColor = "bg-blue";
  }

  function getStartHeight() {
    return Math.max(window.innerHeight, wrapperHeight);
  }

  function onUpdate(progress: number) {
    const p = gsap.utils.clamp(0, 1, gsap.utils.mapRange(0, 1, 0, 1, progress));
    gsap.set(inner, { scale: 1 - 0.1 * p });
    gsap.set(inner, { y: wrapperHeight * (p * 0.4) });
  }
  onMount(() => {
    resize();
    desktopMediaQuery = window.matchMedia(
      `(min-width: ${BREAKPOINTS.small}px)`,
    );
    desktopMediaQuery.addEventListener("change", onMediaQueryChange);

    onMediaQueryChange();

    if (theme === "green") {
      backgroundColor = "bg-green";
    } else if (theme === "yellow") {
      backgroundColor = "bg-yellow";
    } else {
      backgroundColor = "bg-blue";
    }

    return () => {
      killScroller();
      desktopMediaQuery.removeEventListener("change", onMediaQueryChange);
    };
  });

  function onMediaQueryChange() {
    isMobile = !desktopMediaQuery.matches;

    if (!isMobile) {
      createScroller();
    } else {
      killScroller();
    }
  }

  function createScroller() {
    scroller = new ScrollTrigger({
      trigger: wrapper,
      start: () => `${getStartHeight()} bottom`,
      end: "bottom top",
      scrub: 1,
      pinSpacing: false,
      anticipatePin: 0.5,
      onUpdate: (e) => onUpdate(e.progress),
    });

    onUpdate(0);
  }

  function killScroller() {
    scroller && scroller.kill();
  }

  function resize() {
    wrapperHeight = wrapper.getBoundingClientRect().height;
    if (scroller) {
      scroller.refresh();
    }
  }

  const hasCustomBackLink = (blok?.customBackLink?.length ?? 0) > 0;
</script>

<svelte:window on:resize={resize} />

<div
  bind:this={wrapper}
  class={`section hero max-w-full ${theme} `}
  use:storyblokEditable={blok}
>
  <div bind:this={inner} class="movable">
    <div class="section-inner grid gap-[70px] {backgroundColor}">
      <GridContainer class="section-max-width" alignItems="start">
        <GridItem col={12} colMd={12}>
          <div class="grid md:grid-cols-12 gap-[24px]">
            <div class="md:col-span-9">
              {#if blok.isMain}
                <h1 class="headline type:h1">{blok.headline}</h1>
              {:else}
                <a
                  href={hasCustomBackLink
                    ? parseUrl(blok.customBackLink?.[0]?.link ?? "")
                    : "/newsroom"}
                  class="inline-flex text-white type:h6-medium no-underline items-center gap-[.75rem] group"
                >
                  <div
                    class="group-hover:-translate-x-1 translate-x-0 transition-transform"
                  >
                    <div>
                      <svg
                        class="w-[12px] h-[12px]"
                        width="auto"
                        height="auto"
                        viewBox="0 0 16 17"
                        fill="none"
                      >
                        <path
                          d="M8 15.5L1 8.5L8 1.5"
                          stroke="currentColor"
                          stroke-width="1.5"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M16 8.5L1 8.5"
                          stroke="currentColor"
                          stroke-width="1.5"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                  <div class="relative md:top-[1px]">
                    {hasCustomBackLink
                      ? blok.customBackLink?.[0].text
                      : "Newsroom"}
                  </div>
                </a>
              {/if}
            </div>

            {#if blok.subHeadline}
              <div
                class="md:col-span-3 md:text-right flex flex-col items-end justify-center"
              >
                <h3 class="type:h6-small text-white">{blok.subHeadline}</h3>
                <a
                  href={blok?.subHeadlineLink?.url?.includes("@")
                    ? "mailto:" + blok.subHeadlineLink.url
                    : blok.subHeadlineLink?.url}
                  class="type:body-small text-white"
                  >{blok.subHeadlineLink?.url}
                </a>
              </div>
            {/if}
          </div>

          <div
            class="grid md:grid-cols-4 gap-[32px] lg:gap-[64px] mt-6 md:mt-[50px]"
          >
            <div>
              {#if !blok.isMain}
                <h1 class="headline type:h1 mb-5">{@html blok.headline}</h1>
              {/if}

              {#if blok.text}
                <h3 class="type:body-large text-white/80">{@html blok.text}</h3>
              {/if}

              {#if blok.anchors && blok.anchors.length > 0}
                <nav class="grid gap-[1.5rem] xl:gap-[1.75rem] md:max-w-[90%]">
                  {#each blok.anchors as anchor}
                    <a
                      class="body-small no-underline pb-[.75rem] xl:pb-[1.25rem] block group"
                      href={`/${anchor.link?.cached_url}`}
                      style="border-bottom:1px solid rgba(255,255,255,.9)"
                    >
                      <div
                        class="flex text-white mb-[.5rem] items-center justify-between"
                      >
                        <h2
                          class="type:h6-small text-white transition-colors group-hover:text-white/80"
                        >
                          {anchor.label}
                        </h2>
                        <Icon type={IconType.ARROW_RIGHT} />
                      </div>
                      <div
                        class="type:body-small text-white/80 transition-colors max-w-[90%] group-hover:text-white/60"
                      >
                        {anchor.text}
                      </div>
                    </a>
                  {/each}
                </nav>
              {/if}
            </div>
            <div class="md:col-span-3">
              <div class="relative tilted-hero">
                <div class="tilted-wrapper">
                  <TiltedGallery
                    blok={[{ aspectRatio: "", gallery: blok.carousel }]}
                    backgroundColor={theme}
                    activeBackgroundColor={theme}
                    mode="hero-newsroom"
                    useDefaultButtonColor={false}
                    cardsShown={3}
                    buttonColor={ButtonColor.WHITE}
                    disableButtonHoverAnimation={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </GridItem>
      </GridContainer>
    </div>
  </div>
</div>

<style lang="scss">
  .hero {
    padding-top: 0;
    padding: 0 20px;
    position: relative;
    &.yellow {
      *,
      h1 {
        @apply text-charcoal;
      }
    }
  }
  .section-inner {
    color: #ffffff;
    border-radius: var(--border-radius-section);
    padding-top: 60px;
    padding-bottom: 60px;
    @screen lg {
      padding-top: 74px;
      padding-bottom: 80px;
    }

    .headline {
      color: #ffffff;
    }
  }

  .tilted-hero {
    perspective: 1000px;
    display: flex;
    flex-direction: column;
    @screen md {
      aspect-ratio: 894 / 486;
    }
  }

  .tilted-wrapper {
    transform-style: preserve-3d;
    transition: transform 1s;

    @screen md {
      height: 420px;
    }
  }

  .pagination-container {
    display: flex;
    justify-content: center;
    width: 100%;
  }
</style>
